import { Eye, MessageSquare } from 'lucide-react';
import { useSession } from 'next-auth/react';

import type { ReaderType } from '@/types';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { FollowUserButton } from '@/features/notifications/components/follow-user-button';
import { ShareButtons } from './share-buttons';
import { DonationButton } from '@/components/donation-button';

interface ChapterHeaderProps {
  type: ReaderType;
  title: string;
  views: number;
  comments: number;
  author?: {
    id?: number;
    name?: string;
    username?: string;
    avatar?: {
      url?: string;
      thumbnailURL?: string;
    };
    donation?: {
      donationUserId?: string | null;
      hasCard: boolean;
    };
  } | null;
  linkToShare: string;
  onSupportClick?: () => void;
  hideSupportButton?: boolean;
  shareButtonsVariant?: 'default' | 'preview';
  textColor?: string;
}

const getInitials = (name: string) => {
  if (!name || typeof name !== 'string') return '?';
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const ChapterHeader = ({
  type,
  title,
  views,
  comments,
  author,
  linkToShare,
  shareButtonsVariant = 'default',
  textColor = '#1B1D22',
}: ChapterHeaderProps) => {
  const authorName = author?.name || author?.username || 'author';
  const authorAvatar = author?.avatar?.thumbnailURL || author?.avatar?.url;

  const { data: session } = useSession();

  return (
    <div className="text-center">
      <p
        className="break-words font-black text-[20px] uppercase leading-[115%] [overflow-wrap:anywhere]"
        style={{ color: textColor }}
      >
        {title}
      </p>

      {type === 'full' && (
        <>
          <div className="text-[#898989] flex justify-center gap-3 items-center text-xs leading-[140%] font-medium mt-3">
            <div className="flex items-center gap-1">
              <Eye className="size-4" /> <p>{views}</p>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="size-4" fill="#898989" /> <p>{comments}</p>
            </div>
          </div>

          {author && (
            <div className="flex items-center justify-center gap-1 mt-2">
              <Avatar className="h-10 w-10">
                <AvatarImage src={authorAvatar} alt={authorName} />
                <AvatarFallback className="bg-[#898989] text-white text-sm font-medium">
                  {getInitials(authorName)}
                </AvatarFallback>
              </Avatar>
              <span className="text-base font-medium" style={{ color: textColor }}>
                от {authorName}
              </span>
              {author.id != null && <FollowUserButton userId={author.id} compact />}
              <DonationButton author={author} currentUserId={session?.user?.id} variant="small" />
            </div>
          )}

          <ShareButtons title={title} linkToShare={linkToShare} variant={shareButtonsVariant} />
        </>
      )}
    </div>
  );
};
